import React from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Loader2 } from 'lucide-react';
import { ImageUploader } from '../../../ui/image-uploader';
import { AIButton } from '../../../ui/ai-button';
import { toast } from '../../../../lib/toast-utils';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { useAIGeneration } from '../../../../hooks/useAIGeneration';
import { 
  ProjectHighlightsContent, 
  Feature, 
  Developer,
  FileId 
} from '../../../../types/pageBuilder';
import { Id } from '../../../../convex/_generated/dataModel';

interface ProjectHighlightsSectionProps {
  projectId: string;
  selectedLanguage: string;
  projectHighlightsComponent: any;
  projectHighlightsContent: ProjectHighlightsContent;
  setProjectHighlightsContent: React.Dispatch<React.SetStateAction<ProjectHighlightsContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const ProjectHighlightsSection: React.FC<ProjectHighlightsSectionProps> = ({
  projectId,
  selectedLanguage,
  projectHighlightsComponent,
  projectHighlightsContent,
  setProjectHighlightsContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { generate, isGenerating } = useAIGeneration();
  
  // Mutations
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');
  
  // Save Project Highlights component
  const saveProjectHighlightsComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving project highlights component with data:', projectHighlightsContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'projectHighlights',
        order: 200, // Second component after hero
        content: projectHighlightsContent,
        isActive: true,
        componentId: projectHighlightsComponent?._id
      });
      
      console.log('Project highlights component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Project highlights section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving project highlights component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save project highlights'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add a new feature
  const addFeature = () => {
    setProjectHighlightsContent(prev => ({
      ...prev,
      features: [
        ...prev.features, 
        { 
          icon: 'location', 
          title: '', 
          description: '' 
        }
      ]
    }));
  };

  // Remove a feature
  const removeFeature = (index: number) => {
    setProjectHighlightsContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Update a feature
  const updateFeature = (index: number, field: keyof Feature, value: string) => {
    setProjectHighlightsContent(prev => {
      const newFeatures = [...prev.features];
      newFeatures[index] = {
        ...newFeatures[index],
        [field]: value
      };
      return {
        ...prev,
        features: newFeatures
      };
    });
  };
  
  // Update developer info
  const updateDeveloper = (field: keyof Developer, value: string) => {
    setProjectHighlightsContent(prev => ({
      ...prev,
      developer: {
        ...prev.developer!,
        [field]: value
      }
    }));
  };

  // AI Generation handlers
  const handleGenerateFeatureTitle = async (index: number) => {
    if (!projectHighlightsContent.features[index]) return;
    
    try {
      const generated = await generate({
        prompt: `Generate a compelling feature title for a real estate project. Current feature: ${JSON.stringify(projectHighlightsContent.features[index])}`,
        context: `Feature ${index + 1} title generation`,
        fieldType: 'title',
        language: selectedLanguage
      });
      
      if (generated) {
        updateFeature(index, 'title', generated);
      }
    } catch (error) {
      console.error('Error generating feature title:', error);
    }
  };

  const handleGenerateFeatureDescription = async (index: number) => {
    if (!projectHighlightsContent.features[index]) return;
    
    try {
      const generated = await generate({
        prompt: `Generate a compelling feature description for a real estate project. Feature title: ${projectHighlightsContent.features[index].title}`,
        context: `Feature ${index + 1} description generation`,
        fieldType: 'description',
        language: selectedLanguage
      });
      
      if (generated) {
        updateFeature(index, 'description', generated);
      }
    } catch (error) {
      console.error('Error generating feature description:', error);
    }
  };

  const handleGenerateDeveloperDescription = async () => {
    if (!projectHighlightsContent.developer?.name) {
      toast({
        title: 'Error',
        description: 'Please enter developer name first',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const generated = await generate({
        prompt: `Generate a professional developer description for ${projectHighlightsContent.developer.name}`,
        context: 'Developer description generation',
        fieldType: 'description',
        language: selectedLanguage
      });
      
      if (generated) {
        updateDeveloper('description', generated);
      }
    } catch (error) {
      console.error('Error generating developer description:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Highlights</CardTitle>
        <CardDescription>
          Configure the project highlights and developer information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Developer Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Developer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="developer-name">Developer Name</Label>
              <Input
                id="developer-name"
                value={projectHighlightsContent.developer?.name || ''}
                onChange={(e) => updateDeveloper('name', e.target.value)}
                placeholder="Emaar Properties"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="developer-logo">Developer Logo</Label>
              {projectId && (
                <ImageUploader
                  projectId={projectId as Id<"projects">}
                  componentId={projectHighlightsComponent?._id}
                  purpose="developer-logo"
                  altText="Developer Logo"
                  existingImageUrl={projectHighlightsContent.developer?.logo || ''}
                  onUploadComplete={(fileId, url) => updateDeveloper('logo', url)}
                  maxSizeMB={10}
                  acceptedTypes={['image/jpeg', 'image/png', 'image/svg+xml']}
                  enableCompression={true}
                  compressionThresholdKB={100}
                />
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="developer-description">Developer Description</Label>
            <div className="flex gap-2 items-start">
              <Textarea
                id="developer-description"
                value={projectHighlightsContent.developer?.description || ''}
                onChange={(e) => updateDeveloper('description', e.target.value)}
                placeholder="Dubai's premier real estate developer with a proven track record..."
                rows={3}
                className="flex-1"
              />
              <AIButton
                onClick={handleGenerateDeveloperDescription}
                isLoading={isGenerating}
                size="sm"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="developer-video">Video URL (YouTube embed link)</Label>
            <Input
              id="developer-video"
              value={projectHighlightsContent.developer?.videoUrl || ''}
              onChange={(e) => updateDeveloper('videoUrl', e.target.value)}
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
            />
            <p className="text-xs text-gray-500">
              Use YouTube embed URL format: https://www.youtube.com/embed/VIDEO_ID
            </p>
          </div>
        </div>
        
        {/* Features Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Project Features</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addFeature}
              type="button"
            >
              Add Feature
            </Button>
          </div>
          
          {projectHighlightsContent.features.map((feature, index) => (
            <div key={index} className="border rounded-md p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium">Feature {index + 1}</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeFeature(index)}
                  className="h-8 text-red-500 hover:text-red-700"
                >
                  Remove
                </Button>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label htmlFor={`feature-icon-${index}`}>Icon</Label>
                  <Select 
                    value={feature.icon} 
                    onValueChange={(value) => updateFeature(index, 'icon', value)}
                  >
                    <SelectTrigger id={`feature-icon-${index}`}>
                      <SelectValue placeholder="Select icon" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="location">Location</SelectItem>
                      <SelectItem value="building">Building</SelectItem>
                      <SelectItem value="payment">Payment</SelectItem>
                      <SelectItem value="calendar">Calendar</SelectItem>
                      <SelectItem value="investment">Investment</SelectItem>
                      <SelectItem value="lifestyle">Lifestyle</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="amenities">Amenities</SelectItem>
                      <SelectItem value="view">View</SelectItem>
                      <SelectItem value="sustainability">Sustainability</SelectItem>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="community">Community</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                      <SelectItem value="pool">Pool</SelectItem>
                      <SelectItem value="gym">Gym</SelectItem>
                      <SelectItem value="garden">Garden</SelectItem>
                      <SelectItem value="parking">Parking</SelectItem>
                      <SelectItem value="playground">Playground</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="spa">Spa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor={`feature-title-${index}`}>Title</Label>
                  <div className="flex gap-2">
                    <Input
                      id={`feature-title-${index}`}
                      value={feature.title}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                      placeholder="Prime Location"
                      className="flex-1"
                    />
                    <AIButton
                      onClick={() => handleGenerateFeatureTitle(index)}
                      isLoading={isGenerating}
                      size="sm"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor={`feature-description-${index}`}>Description</Label>
                  <div className="flex gap-2 items-start">
                    <Textarea
                      id={`feature-description-${index}`}
                      value={feature.description}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                      placeholder="Located in the heart of Dubai, minutes away from iconic landmarks"
                      rows={2}
                      className="flex-1"
                    />
                    <AIButton
                      onClick={() => handleGenerateFeatureDescription(index)}
                      isLoading={isGenerating}
                      size="sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {projectHighlightsContent.features.length === 0 && (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-sm text-gray-500">No features added yet. Click the button above to add a feature.</p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setProjectHighlightsContent({
            features: [],
            developer: {
              name: '',
              description: '',
              logo: '',
              videoUrl: ''
            }
          })}
          disabled={isLoading}
        >
          Reset
        </Button>
        <Button 
          onClick={saveProjectHighlightsComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save Project Highlights'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 